/**
 * Import / Export del vault zero-knowledge.
 *
 * IMPORT: parsea un CSV exportado por otro gestor (formato genérico + mapeos
 * comunes: Bitwarden, Chrome/navegador) a `VaultItemPlain[]`. El cifrado al
 * vault ocurre DESPUÉS, en el cliente (con `crypto.encryptItem`): estas
 * funciones nunca tocan el servidor ni claves.
 *
 * EXPORT cifrado (respaldo zero-knowledge): sella TODOS los items en un blob
 * que solo se abre con una passphrase (Argon2id → clave de respaldo → AES-GCM).
 * El servidor jamás lo ve; es portátil y re-importable (round-trip probado).
 *
 * EXPORT en claro (portabilidad): CSV legible por otros gestores, PROTEGIDO
 * contra inyección de fórmulas CSV (celdas que empiezan por = + - @ / TAB / CR
 * se neutralizan con `'`). Requiere confirmación fuerte en la UI (expone plano).
 *
 * INVARIANTE (probado en `__tests__/import-export.test.ts`): el blob de respaldo
 * cifrado NO contiene ningún plano ni la clave de respaldo, en ninguna
 * codificación (base64/hex). Round-trip import→export→import conserva los datos.
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "../crypto/encoding";
import {
  type Argon2Params,
  deriveMasterKey,
  type KdfConfig,
  KDF_PRESETS,
  newKdfSaltBytes,
} from "../crypto/kdf";
import { hkdfExpand, openBase64, sealBase64 } from "../crypto/primitives";
import type { VaultItemPlain, VaultItemType } from "../crypto/types";

// ─────────────────────────────────────────────────────────────────────────────
// CSV parsing (RFC 4180: comillas dobles, comas y saltos de línea escapados)
// ─────────────────────────────────────────────────────────────────────────────

/** Parsea un CSV a una matriz de filas de celdas (respeta comillas/escapes). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  // Normaliza CRLF/CR a LF fuera de comillas para no partir mal.
  const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  // Última celda/fila (si el fichero no termina en \n).
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Descarta filas totalmente vacías.
  return rows.filter((r) => r.some((cell) => cell.trim().length > 0));
}

export interface CsvRecords {
  readonly headers: string[];
  readonly records: Record<string, string>[];
}

/** Convierte filas CSV en registros por-cabecera (primera fila = cabeceras). */
export function csvToRecords(text: string): CsvRecords {
  const rows = parseCsv(text);
  const [headerRow, ...dataRows] = rows;
  if (!headerRow) {
    return { headers: [], records: [] };
  }
  const headers = headerRow.map((h) => h.trim().toLowerCase());
  const records = dataRows.map((r) => {
    const rec: Record<string, string> = {};
    headers.forEach((h, idx) => {
      rec[h] = r[idx] ?? "";
    });
    return rec;
  });
  return { headers, records };
}

// ─────────────────────────────────────────────────────────────────────────────
// Detección de formato + mapeo a VaultItemPlain
// ─────────────────────────────────────────────────────────────────────────────

export type CsvImportFormat = "bitwarden" | "chrome" | "generic";

/** Deduce el formato del CSV a partir de sus cabeceras. */
export function detectCsvFormat(headers: string[]): CsvImportFormat {
  const set = new Set(headers);
  if (set.has("login_password") || set.has("login_username") || set.has("login_uri")) {
    return "bitwarden";
  }
  // Chrome/navegadores: name,url,username,password (sin columnas login_*).
  if (set.has("password") && set.has("url") && (set.has("name") || set.has("username"))) {
    return "chrome";
  }
  return "generic";
}

function pick(rec: Record<string, string>, ...keys: string[]): string {
  for (const k of keys) {
    const v = rec[k];
    if (v && v.trim().length > 0) {
      return v;
    }
  }
  return "";
}

function toPlain(
  type: VaultItemType,
  name: string,
  fields: Record<string, string>,
  extra: { totpSecret?: string; notes?: string } = {},
): VaultItemPlain {
  // Descarta campos vacíos (no ensucia el sobre).
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v && v.length > 0) {
      clean[k] = v;
    }
  }
  return {
    type,
    name,
    fields: clean,
    ...(extra.totpSecret ? { totpSecret: extra.totpSecret } : {}),
    ...(extra.notes ? { notes: extra.notes } : {}),
  };
}

function mapRecord(format: CsvImportFormat, rec: Record<string, string>): VaultItemPlain | null {
  if (format === "bitwarden") {
    const type = pick(rec, "type").toLowerCase();
    if (type === "note" || type === "securenote") {
      const name = pick(rec, "name") || "Nota";
      return toPlain("note", name, {}, { notes: pick(rec, "notes") });
    }
    const username = pick(rec, "login_username");
    const password = pick(rec, "login_password");
    const url = pick(rec, "login_uri");
    const name = pick(rec, "name") || url || username;
    if (!name && !username && !password) {
      return null;
    }
    return toPlain(
      "login",
      name || "Login",
      { username, password, url },
      { totpSecret: pick(rec, "login_totp"), notes: pick(rec, "notes") },
    );
  }
  if (format === "chrome") {
    const username = pick(rec, "username");
    const password = pick(rec, "password");
    const url = pick(rec, "url");
    const name = pick(rec, "name") || url || username;
    if (!name && !username && !password) {
      return null;
    }
    return toPlain("login", name || "Login", { username, password, url });
  }
  // Genérico: cabeceras name/username/password/url/totp/notes (case-insensitive).
  const username = pick(rec, "username", "user", "login");
  const password = pick(rec, "password", "pass", "pwd");
  const url = pick(rec, "url", "uri", "website");
  const totp = pick(rec, "totp", "otp", "otpauth");
  const notes = pick(rec, "notes", "note", "comment");
  const name = pick(rec, "name", "title") || url || username;
  const hasLogin = Boolean(username || password || url);
  if (!name && !hasLogin && !notes) {
    return null;
  }
  if (!hasLogin && notes) {
    return toPlain("note", name || "Nota", {}, { notes });
  }
  return toPlain(
    "login",
    name || "Login",
    { username, password, url },
    { totpSecret: totp, notes },
  );
}

export interface CsvImportResult {
  readonly format: CsvImportFormat;
  readonly items: VaultItemPlain[];
  /** Filas descartadas por estar vacías / sin datos utilizables. */
  readonly skipped: number;
}

/**
 * Importa un CSV a items en claro (`VaultItemPlain[]`) listos para cifrar al
 * vault en el cliente. `format` se autodetecta si no se pasa.
 */
export function importCsv(text: string, format?: CsvImportFormat): CsvImportResult {
  const { headers, records } = csvToRecords(text);
  const fmt = format ?? detectCsvFormat(headers);
  const items: VaultItemPlain[] = [];
  let skipped = 0;
  for (const rec of records) {
    const plain = mapRecord(fmt, rec);
    if (plain) {
      items.push(plain);
    } else {
      skipped++;
    }
  }
  return { format: fmt, items, skipped };
}

// ─────────────────────────────────────────────────────────────────────────────
// Export cifrado (respaldo zero-knowledge, re-importable con passphrase)
// ─────────────────────────────────────────────────────────────────────────────

const BACKUP_KIND = "boveda-backup" as const;
const BACKUP_VERSION = 1 as const;
const BACKUP_AAD = "boveda:backup:v1";
const BACKUP_HKDF_INFO = "boveda:backup:v1:key";

/** Formato del blob de respaldo cifrado (portátil, opaco). */
export interface EncryptedBackup {
  readonly kind: typeof BACKUP_KIND;
  readonly v: typeof BACKUP_VERSION;
  /** Parámetros KDF públicos para re-derivar la clave desde la passphrase. */
  readonly kdf: KdfConfig;
  /** Sobre AES-256-GCM (base64) de los items serializados. */
  readonly blob: string;
}

/** Deriva la clave de respaldo (32 B) desde la passphrase + KDF público. */
async function deriveBackupKey(passphrase: string, kdf: KdfConfig): Promise<Uint8Array> {
  const master = deriveMasterKey(passphrase, base64ToBytes(kdf.salt), kdf.params);
  try {
    return await hkdfExpand(master, BACKUP_HKDF_INFO);
  } finally {
    master.fill(0);
  }
}

/**
 * Sella todos los items en un respaldo cifrado con una passphrase. Usa el preset
 * `sensitive` de Argon2id (operación de alto valor). El resultado es opaco: solo
 * quien tenga la passphrase lo abre.
 */
export async function exportEncryptedBackup(
  passphrase: string,
  items: readonly VaultItemPlain[],
  params: Argon2Params = KDF_PRESETS.sensitive,
): Promise<EncryptedBackup> {
  if (passphrase.length === 0) {
    throw new Error("passphrase required");
  }
  const salt = newKdfSaltBytes();
  const kdf: KdfConfig = { algorithm: "argon2id", params, salt: bytesToBase64(salt), v: 1 };
  const key = await deriveBackupKey(passphrase, kdf);
  try {
    const blob = await sealBase64(key, utf8Encode(JSON.stringify(items)), BACKUP_AAD);
    return { kind: BACKUP_KIND, v: BACKUP_VERSION, kdf, blob };
  } finally {
    key.fill(0);
  }
}

/** Abre un respaldo cifrado con su passphrase → items en claro (SOLO en cliente). */
export async function importEncryptedBackup(
  passphrase: string,
  backup: EncryptedBackup,
): Promise<VaultItemPlain[]> {
  if (backup.kind !== BACKUP_KIND || backup.v !== BACKUP_VERSION) {
    throw new Error("unrecognized backup format");
  }
  const key = await deriveBackupKey(passphrase, backup.kdf);
  try {
    const bytes = await openBase64(key, backup.blob, BACKUP_AAD);
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return JSON.parse(utf8Decode(bytes)) as VaultItemPlain[];
  } finally {
    key.fill(0);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Export en claro (CSV) — con protección anti-inyección de fórmulas
// ─────────────────────────────────────────────────────────────────────────────

const PLAINTEXT_HEADERS = ["name", "type", "username", "password", "url", "totp", "notes"] as const;

/**
 * Neutraliza inyección de fórmulas CSV (CWE-1236): una celda que empieza por
 * `= + - @` o por TAB/CR se ejecutaría como fórmula al abrirla en una hoja de
 * cálculo. Se antepone `'` para forzar su interpretación como texto.
 */
export function sanitizeCsvCell(value: string): string {
  if (value.length > 0 && /^[=+\-@\t\r]/.test(value)) {
    return `'${value}`;
  }
  return value;
}

/** Encomilla una celda CSV (RFC 4180) tras neutralizar la inyección. */
function csvField(value: string): string {
  const safe = sanitizeCsvCell(value);
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/**
 * Exporta los items a CSV EN CLARO (portabilidad). Peligroso: expone secretos.
 * La UI DEBE pedir confirmación fuerte. Protegido contra inyección de fórmulas.
 */
export function exportPlaintextCsv(items: readonly VaultItemPlain[]): string {
  const lines = [PLAINTEXT_HEADERS.join(",")];
  for (const item of items) {
    const cells = [
      item.name,
      item.type,
      item.fields.username ?? "",
      item.fields.password ?? "",
      item.fields.url ?? "",
      item.totpSecret ?? "",
      item.notes ?? "",
    ];
    lines.push(cells.map(csvField).join(","));
  }
  return lines.join("\n");
}
